import type { CandidateRejectionReason } from "../../common/types/CandidateRejectionReason";
import { api } from "../../app/api";
import filterFalsyParameters from "../../common/helpers/filterFalsyParameters";
import { LocalDate } from "../../common/types/commonTypes";
import { EventPayload } from "../../common/types/Event";
import { Options } from "../../common/types/requestOptions";
import { JobAdApplicant } from "../jobAds/jodAdApplicants/jobAdApplicantsAPI";
import { TalentStatus } from "../talents/types/common";
import { CandidateStatus } from "./editOrder/constants";
import { OrderRowBundleId } from "./orderAPI";

// TODO: check all types and move to correct place
export interface Candidate {
  talentId: string;
  firstName: string;
  lastName: string;
  applieadAt?: EpochTimeStamp;
  status: TalentStatus;
  workAgreementId?: string;
  picked?: CandidateStatusDetails;
  interestAsked?: CandidateStatusDetails;
  interested?: CandidateStatusDetails;
  introduced?: CandidateStatusDetails;
  chosenForInterview?: CandidateStatusDetails;
  customerApproved?: CandidateStatusDetails;
  offered?: CandidateStatusDetails;
  workAgreementAccepted?: CandidateStatusDetails;
  eezyRejected?: CandidateStatusDetails;
  withdrew?: CandidateStatusDetails;
  rejectionMessageSent?: CandidateStatusDetails;
  hasCoverLetter?: boolean;
  statusChanged?: LocalDate;
  llt?: EpochTimeStamp;
  overlappingAssignments?: OverlappingAssignment[];
  exclusivityEntryIds?: string[];
  matchingDetails?: CandidateMatchingDetails;
  /** Distance from home to the work location in meters (rounded to the nearest meter), if known. */
  distanceToWorkLocation?: number | null;
}

export interface OverlappingAssignment {
  assignmentId: string;
  start: LocalDate;
  end?: LocalDate;
}

export interface CandidateMatchingDetails {
  workTitle: CandidateMatchDetail;
  language: CandidateMatchDetail;
  skill: CandidateMatchDetail;
  training: CandidateMatchDetail;
  certificate: CandidateMatchDetail;
  workRight: CandidateMatchDetail;
  exclusivity: CandidateMatchDetail;
  availability: CandidateMatchDetail;
  blacklisting: CandidateMatchDetail;
  salary: CandidateMatchDetail;
}

export interface CandidateMatchDetail {
  match: boolean;
  flags?: MatchingDetailsFlag[];
}

export type MatchingDetailsFlag =
  | "WorkTitleMissing"
  | "TrainingMissing"
  | "TrainingIncomplete"
  | "CertificateMissing"
  | "CertificateIncomplete"
  | "LanguageSkillMissing"
  | "LanguageSkillLevelIncomplete"
  | "SkillMissing";

export interface FetchCandidatesRequest {
  bundleId: string;
  recommended: boolean;
  picked: boolean;
  applicants: boolean;
}

// TODO: these talents should have statusChanged as well
export interface FetchCandidatesResult {
  recommended: Candidate[];
  picked: PickedCandidate[];
  applicants: JobAdApplicant[];
}

export interface RecommendedCandidatesFilters {
  bundleId: string;
  size?: number;
  talentStatus?: TalentStatus[];
  details?: boolean;
  excludeCandidates?: string[]; // comma separated list of talentIds, which are not returned in this query
}

export interface RecommendedCandidates {
  candidates: Candidate[];
}

export interface CandidatePicked {
  talentId: string;
}

export interface CandidateRejected {
  talentId: string;
  rejectionReason?: CandidateRejectionReason | null;
  rejectionComment?: string | null;
}

export interface CandidateRejectionCancelled {
  talentId: string;
}

export interface CandidateRejectionMessageSent {
  talentId: string;
}

export interface PickCandidate {
  bundleId: string;
  talentIds: string[];
}

export interface RejectCandidate {
  bundleId: string;
  talentIds: string[];
  rejectionReason?: CandidateRejectionReason | null;
  rejectionComment?: string | null;
}

export interface CancelCandidateRejection {
  bundleId: string;
  talentIds: string[];
}

export interface AskWorkInterest {
  bundleId: string;
  talentIds: string[];
}

export interface SendCandidateRejectedMessagePayload {
  bundleId: string;
  talentIds: string[];
  message: string;
}

// get talent details from the action.meta.arg parameter of the pickCandidates thunk
export interface PickCandidateRedux {
  bundleId: string;
  talents: LongOrderManualTalentSearchResult[];
}

export interface CandidateStatusDetails {
  userId: string;
  firstName: string;
  lastName: string;
  at: EpochTimeStamp;
  success?: boolean;
  rejectionReason?: CandidateRejectionReason | null;
  rejectionComment?: string | null;
}

export interface CandidateRejectedMessageDetails {
  userId?: string;
  firstName?: string;
  lastName?: string;
  at?: EpochTimeStamp;
  message?: string;
}

export interface PickedCandidate
  extends Candidate,
    Partial<Record<CandidateStatus, CandidateStatusDetails>> {}

export interface PickedCandidateWithNewestStatus extends PickedCandidate {
  newestStatus: CandidateStatus;
}

export interface PickedCandidates {
  candidates: PickedCandidate[];
}

export interface CandidateIntroducedToCustomer {
  talentId: string;
}

export interface CandidateChosenForInterview {
  talentId: string;
}

export interface CandidateApprovedByCustomer {
  talentId: string;
}

export interface CandidateWithdrew {
  talentId: string;
}

export interface CandidateWithdrawalCancelled {
  talentId: string;
}

export interface CandidateInterestAsked {
  talentId: string;
}

export interface IntroduceTalentsToCustomer {
  bundleId: string;
  talentIds: string[];
}

export interface ChooseCandidatesForInterview {
  bundleId: string;
  talentIds: string[];
}

export interface WithdrawCandidate {
  bundleId: string;
  talentIds: string[];
}

export interface ApproveIntroducedTalent {
  bundleId: string;
  talentId: string;
  approved: boolean;
  rejectionReason?: CandidateRejectionReason | null;
  rejectionComment?: string | null;
}

export interface CancelCandidateWithdrawal {
  bundleId: string;
  talentIds: string[];
}

export interface CancelTalentIntroductions extends IntroduceTalentsToCustomer {}

export interface CandidateIntroductionsCancelled {
  talentIds: string[];
}

export const pickedCandidates = async (
  bundleId: string,
  { requestId }: Options,
): Promise<PickedCandidates> =>
  await api
    .get<PickedCandidates>("/order/qry/pickedCandidates", {
      requestId,
      auth: true,
      queryParams: { bundleId },
    })
    .then((res) => res.data);

export interface LongOrderManualTalentSearchFilters {
  bundleId: OrderRowBundleId;
  search: string;
  excludeCandidates?: string[]; // talentIds
}

export interface LongOrderManualTalentSearchResult {
  talentId: string;
  firstName: string;
  lastName: string;
  hasOverlappingAssignment: boolean;
  status: TalentStatus;
  statusChanged?: LocalDate;
  exclusivityEntryIds?: string[];
}

export const recommendedCandidates = async (
  filters: RecommendedCandidatesFilters,
  { requestId }: Options,
): Promise<RecommendedCandidates> =>
  await api
    .get<RecommendedCandidates>("/order/qry/recommendedCandidates", {
      requestId,
      auth: true,
      queryParams: {
        ...filterFalsyParameters({
          ...filters,
          talentStatus: filters.talentStatus?.join(","),
        }),
      },
    })
    .then((res) => res.data);

export const pickCandidate = async (
  body: PickCandidate,
  { requestId }: Options,
): Promise<EventPayload<CandidatePicked>[]> =>
  await api
    .post<EventPayload<CandidatePicked>[]>("/order/cmd/pickCandidate", {
      requestId,
      auth: true,
      body,
    })
    .then((res) => res.data);

export const introduceTalentsToCustomer = async (
  body: IntroduceTalentsToCustomer,
  { requestId }: Options,
): Promise<EventPayload<CandidateIntroducedToCustomer>[]> =>
  await api
    .post<EventPayload<CandidateIntroducedToCustomer>[]>(
      "/order/cmd/introduceTalentsToCustomer",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data);

export const chooseCandidatesForInterview = async (
  body: ChooseCandidatesForInterview,
  { requestId }: Options,
): Promise<EventPayload<CandidateChosenForInterview>[]> =>
  await api
    .post<EventPayload<CandidateChosenForInterview>[]>(
      "/order/cmd/chooseCandidatesForInterview",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data);

export const approveIntroducedTalent = async (
  body: ApproveIntroducedTalent,
  { requestId }: Options,
): Promise<EventPayload<CandidateApprovedByCustomer>[]> =>
  await api
    .post<EventPayload<CandidateApprovedByCustomer>[]>(
      "/order/cmd/approveIntroducedTalent",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data);

export const withdrawCandidate = async (
  body: WithdrawCandidate,
  { requestId }: Options,
): Promise<EventPayload<CandidateWithdrew>[]> =>
  await api
    .post<EventPayload<CandidateWithdrew>[]>("/order/cmd/withdrawCandidate", {
      requestId,
      auth: true,
      body,
    })
    .then((res) => res.data);

export const cancelCandidateWithdrawal = async (
  body: CancelCandidateWithdrawal,
  { requestId }: Options,
): Promise<EventPayload<CandidateWithdrawalCancelled>[]> =>
  await api
    .post<EventPayload<CandidateWithdrawalCancelled>[]>(
      "/order/cmd/cancelCandidateWithdrawal",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data);

export const cancelTalentIntroductions = async (
  body: CancelTalentIntroductions,
  { requestId }: Options,
): Promise<EventPayload<CandidateIntroductionsCancelled>[]> => // TODO: add event type
  await api
    .post<EventPayload<CandidateIntroductionsCancelled>[]>(
      "/order/cmd/cancelTalentIntroductions",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data);

export const rejectCandidate = (
  body: RejectCandidate,
  { requestId }: Options,
): Promise<EventPayload<CandidateRejected>[]> =>
  api
    .post<EventPayload<CandidateRejected>[]>("/order/cmd/rejectCandidate", {
      requestId,
      auth: true,
      body,
    })
    .then((res) => res.data);

export const cancelCandidateRejection = (
  body: CancelCandidateRejection,
  { requestId }: Options,
): Promise<EventPayload<CandidateRejectionCancelled>[]> =>
  api
    .post<EventPayload<CandidateRejectionCancelled>[]>(
      "/order/cmd/cancelCandidateRejection",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data);

export const sendCandidateRejectedMessage = (
  body: SendCandidateRejectedMessagePayload,
  { requestId }: Options,
): Promise<EventPayload<CandidateRejectionMessageSent>[]> =>
  api
    .post<EventPayload<CandidateRejectionMessageSent>[]>(
      "/order/cmd/sendCandidateRejectedMessage",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data);

export const askWorkInterestRequest = (
  body: AskWorkInterest,
  { requestId }: Options,
): Promise<EventPayload<CandidateInterestAsked>[]> =>
  api
    .post<EventPayload<CandidateInterestAsked>[]>(
      "/order/cmd/askWorkInterest",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data);

export const longOrderManualTalentSearchQuery = async (
  filters: LongOrderManualTalentSearchFilters,
  { requestId }: Options,
): Promise<LongOrderManualTalentSearchResult[]> =>
  await api
    .get<LongOrderManualTalentSearchResult[]>(
      "/order/qry/longOrderManualTalentSearch",
      {
        requestId,
        auth: true,
        queryParams: {
          ...filterFalsyParameters(filters),
        },
      },
    )
    .then((res) => res.data);

export const candidateRejectedMessageQuery = async (
  bundleId: string,
  talentId: string,
  { requestId }: Options,
): Promise<CandidateRejectedMessageDetails> =>
  await api
    .get<CandidateRejectedMessageDetails>(
      "/order/qry/candidateRejectedMessage",
      {
        requestId,
        auth: true,
        queryParams: { bundleId, talentId },
      },
    )
    .then((res) => res.data);
